import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  Badge,
  Text,
  useToast,
  Skeleton,
  IconButton,
  Tooltip,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Input,
  FormControl,
  FormLabel,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Card,
  CardBody,
  useColorModeValue,
  Flex,
  Heading,
  Link,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Divider,
  Tfoot,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import {
  BsCheckCircle,
  BsXCircle,
  BsPlus,
  BsFileEarmarkText,
  BsTrophy,
  BsChevronDown,
  BsChevronRight,
  BsEye,
  BsBoxArrowUpRight,
  BsLock,
} from "react-icons/bs";
import vendorPOApi from "../services/vendorPOApi";
import AddVendorPOModal from "./AddVendorPOModal";
import { useAuth } from "../../../context/AuthContext";

const VendorPOProposalList = ({
  requestId,
  requestStatus,
  approvalHistory = [],
  onStatusChange,
}) => {
  const toast = useToast();
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState({});
  const [requestLineItems, setRequestLineItems] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all, approved, pending, rejected
  const [expandedProposalIds, setExpandedProposalIds] = useState([]);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailProposal, setDetailProposal] = useState(null);

  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const cancelRef = React.useRef();

  // Approve confirm dialog
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const approveCancelRef = React.useRef();

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Calculate first approver ID from approval history
  const firstApproverId = React.useMemo(() => {
    if (!approvalHistory || approvalHistory.length === 0) return null;

    // Find the first APPROVE or BENCH_APPROVE action sorted by stage
    const approvals = approvalHistory
      .filter((h) => h.action === "APPROVE" || h.action === "BENCH_APPROVE")
      .sort((a, b) => (a.stage || 0) - (b.stage || 0));

    return approvals.length > 0
      ? approvals[0].approverId?._id || approvals[0].approverId
      : null;
  }, [approvalHistory]);

  useEffect(() => {
    if (requestId) {
      fetchProposals();
    }
  }, [requestId]);

  const fetchProposals = async () => {
    try {
      setLoading(true);
      const response = await vendorPOApi.getVendorPOProposals(requestId);
      setProposals(response?.data?.proposals || []);
      setStats(response?.data?.stats || {});
      setRequestLineItems(response?.data?.requestLineItems || []);
    } catch (error) {
      console.error("❌ Error fetching proposals:", error);
      // Don't show error toast if no proposals exist yet
      if (error.response?.status !== 404) {
        toast({
          title: "Error fetching proposals",
          description: error.response?.data?.message || error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (proposal) => {
    setSelectedProposal(proposal);
    setApproveConfirmOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedProposal) return;

    try {
      setActionLoading(selectedProposal._id);
      await vendorPOApi.approveVendorPOProposal(
        selectedProposal._id,
        requestId,
      );
      toast({
        title: "Proposal approved",
        description:
          "Vendor selected successfully. All other proposals have been rejected.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setApproveConfirmOpen(false);
      setSelectedProposal(null);
      fetchProposals();
      // Notify parent that status changed to PO_SELECTED
      if (onStatusChange) {
        onStatusChange("PO_SELECTED");
      }
    } catch (error) {
      toast({
        title: "Error approving proposal",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleActiveStatusChange = async (proposal, nextStatus) => {
    const isActive = nextStatus === "active";

    try {
      setActionLoading(`toggle-${proposal._id}`);
      await vendorPOApi.updateVendorPOProposalActiveStatus(
        proposal._id,
        isActive,
      );
      toast({
        title: `Proposal ${isActive ? "activated" : "deactivated"}`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchProposals();
    } catch (error) {
      toast({
        title: "Error updating proposal status",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const toggleProposalExpanded = (proposalId) => {
    setExpandedProposalIds((currentIds) =>
      currentIds.includes(proposalId)
        ? currentIds.filter((id) => id !== proposalId)
        : [...currentIds, proposalId],
    );
  };

  const openDetailModal = (proposal) => {
    setDetailProposal(proposal);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetailProposal(null);
  };

  const handleRejectClick = (proposal) => {
    setSelectedProposal(proposal);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedProposal || !rejectionReason.trim()) {
      toast({
        title: "Rejection reason required",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    try {
      setActionLoading(selectedProposal._id);
      await vendorPOApi.rejectVendorPOProposal(
        selectedProposal._id,
        rejectionReason,
      );
      toast({
        title: "Proposal rejected",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setRejectDialogOpen(false);
      setSelectedProposal(null);
      setRejectionReason("");
      fetchProposals();
    } catch (error) {
      toast({
        title: "Error rejecting proposal",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddSuccess = () => {
    onClose();
    fetchProposals();
  };

  const formatCurrency = (amount, currency = "INR") => {
    const symbols = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };
    return `${symbols[currency] || "₹"}${amount?.toLocaleString() || 0}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      SUBMITTED: { label: "Pending", bg: "orange.100", color: "orange.800" },
      APPROVED: { label: "Approved", bg: "green.100", color: "green.800" },
      REJECTED: { label: "Rejected", bg: "red.100", color: "red.800" },
    };
    const config = statusConfig[status] || {
      label: status,
      bg: "gray.100",
      color: "gray.700",
    };
    return (
      <Badge
        px={2.5}
        py={1}
        borderRadius="6px"
        fontSize="12px"
        fontWeight="500"
        bg={config.bg}
        color={config.color}
      >
        {config.label}
      </Badge>
    );
  };

  const getActiveBadge = (isActive) => (
    <Badge
      px={2.5}
      py={1}
      borderRadius="6px"
      fontSize="12px"
      fontWeight="500"
      bg={isActive ? "green.50" : "gray.100"}
      color={isActive ? "green.700" : "gray.700"}
    >
      {isActive ? "Active" : "Inactive"}
    </Badge>
  );

  const getLineItemsTotal = (lineItems = []) =>
    lineItems.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0);

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  let visibleProposals = showInactive
    ? proposals
    : proposals.filter((proposal) => proposal.isActive !== false);

  // Apply status filter
  if (statusFilter === "approved") {
    visibleProposals = visibleProposals.filter((p) => p.status === "APPROVED");
  } else if (statusFilter === "pending") {
    visibleProposals = visibleProposals.filter((p) => p.status === "SUBMITTED");
  } else if (statusFilter === "rejected") {
    visibleProposals = visibleProposals.filter((p) => p.status === "REJECTED");
  }

  // Find approved proposal
  const approvedProposal = proposals.find((p) => p.status === "APPROVED");

  // Check if user can add proposals (allow at any workflow stage, including DRAFT)
  const canAddProposals =
    requestStatus !== "REJECTED" && requestStatus !== "SENT_BACK";

  // Check if user can approve/reject proposals
  // Only the first approver can manage vendor quotations once they have approved
  const isFirstApprover =
    user && firstApproverId && user.id === firstApproverId;
  const hasFirstApprovalHappened = approvalHistory.some(
    (h) => h.action === "APPROVE" || h.action === "BENCH_APPROVE",
  );
  const canManageProposals =
    hasFirstApprovalHappened &&
    isFirstApprover &&
    requestStatus !== "REJECTED" &&
    requestStatus !== "SENT_BACK" &&
    proposals.some((p) => p.status === "SUBMITTED" && p.isActive);

  if (loading) {
    return (
      <Box>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="200px" />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        mb={4}
        direction={{ base: "column", md: "row" }}
        gap={3}
      >
        <Heading size={{ base: "sm", md: "md" }}>Vendor Quotation</Heading>
        <HStack flexWrap="wrap" gap={2}>
          <Select
            size="sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            width={{ base: "full", sm: "auto" }}
            minW="150px"
          >
            <option value="all">All Quotes</option>
            <option value="approved">Approved Only</option>
            <option value="pending">Pending Only</option>
            <option value="rejected">Rejected Only</option>
          </Select>
          <Button
            size="sm"
            variant={showInactive ? "solid" : "outline"}
            onClick={() => setShowInactive((currentValue) => !currentValue)}
          >
            {showInactive ? "Hide Inactive" : "Show Inactive"}
          </Button>
          {canAddProposals && (
            <Button
              leftIcon={<BsPlus />}
              colorScheme="blue"
              size="sm"
              onClick={onOpen}
            >
              Add Vendor Quotation
            </Button>
          )}
        </HStack>
      </Flex>

      {/* Approved Vendor Highlight Section */}
      {approvedProposal && (
        <Card
          bg="green.50"
          borderColor="green.300"
          borderWidth="2px"
          mb={4}
          shadow="md"
        >
          <CardBody>
            <HStack spacing={3} align="center" flexWrap="wrap">
              <BsTrophy size={28} color="#D4AF37" />
              <VStack align="start" spacing={0} flex={1}>
                <HStack>
                  <Text fontSize="lg" fontWeight="bold" color="green.800">
                    Selected Vendor:
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="green.900">
                    {approvedProposal.vendorId?.name || "Unknown Vendor"}
                  </Text>
                </HStack>
                <HStack spacing={4} flexWrap="wrap">
                  <Text fontSize="sm" color="green.700">
                    Quote:{" "}
                    {formatCurrency(
                      approvedProposal.quotedAmount,
                      approvedProposal.currency,
                    )}
                  </Text>
                  <Text fontSize="sm" color="green.700">
                    PO#: {approvedProposal.poNumber}
                  </Text>
                  {approvedProposal.approvedAt && (
                    <Text fontSize="sm" color="green.600">
                      Approved: {formatDateTime(approvedProposal.approvedAt)}
                    </Text>
                  )}
                </HStack>
              </VStack>
              <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                APPROVED
              </Badge>
            </HStack>
          </CardBody>
        </Card>
      )}

      {/* Statistics */}
      {proposals.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 6 }} spacing={4} mb={4}>
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            size="sm"
          >
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Total Proposals</StatLabel>
                <StatNumber fontSize="lg">{stats.total || 0}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            bg={approvedProposal ? "green.50" : cardBg}
            borderWidth="1px"
            borderColor={approvedProposal ? "green.300" : borderColor}
            size="sm"
          >
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Approved Quotes</StatLabel>
                <StatNumber
                  fontSize="lg"
                  color={approvedProposal ? "green.600" : "gray.500"}
                >
                  {stats.approved || 0}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            size="sm"
          >
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Lowest Quote</StatLabel>
                <StatNumber fontSize="lg" color="green.500">
                  {formatCurrency(stats.lowestQuote)}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            size="sm"
          >
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Highest Quote</StatLabel>
                <StatNumber fontSize="lg" color="red.500">
                  {formatCurrency(stats.highestQuote)}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            size="sm"
          >
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Average Quote</StatLabel>
                <StatNumber fontSize="lg">
                  {formatCurrency(stats.averageQuote)}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            size="sm"
          >
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Active Quotes</StatLabel>
                <StatNumber fontSize="lg">{stats.active || 0}</StatNumber>
                <StatHelpText mb={0}>
                  Inactive: {stats.inactive || 0}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Permission Info Alert */}
      {hasFirstApprovalHappened &&
        !isFirstApprover &&
        proposals.some((p) => p.status === "SUBMITTED" && p.isActive) && (
          <Alert status="info" mb={4} borderRadius="md">
            <AlertIcon as={BsLock} />
            <VStack align="start" spacing={0}>
              <Text fontWeight="semibold">Permission Required</Text>
              <Text fontSize="sm">
                Only the first approver can approve or reject vendor quotations.
              </Text>
            </VStack>
          </Alert>
        )}

      {/* Proposals Table */}
      {proposals.length === 0 ? (
        <Box
          textAlign="center"
          py={8}
          borderWidth="1px"
          borderRadius="md"
          borderColor={borderColor}
        >
          <Text color="gray.500" mb={4}>
            No Vendor Quotation yet.
          </Text>
          {canAddProposals && (
            <Button leftIcon={<BsPlus />} colorScheme="blue" onClick={onOpen}>
              Add First Vendor Quotation Proposal.
            </Button>
          )}
          {requestStatus === "REJECTED" || requestStatus === "SENT_BACK" ? (
            <Text fontSize="sm" color="gray.400" mt={2}>
              Vendor proposals cannot be added for {requestStatus} requests.
            </Text>
          ) : null}
        </Box>
      ) : visibleProposals.length === 0 ? (
        <Box
          textAlign="center"
          py={8}
          borderWidth="1px"
          borderRadius="md"
          borderColor={borderColor}
        >
          <Text color="gray.500" mb={4}>
            All vendor quotations are currently inactive.
          </Text>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowInactive(true)}
          >
            Show Inactive Quotes
          </Button>
        </Box>
      ) : (
        <Box
          borderWidth="1px"
          borderRadius="md"
          borderColor={borderColor}
          overflowX="auto"
        >
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th></Th>
                <Th>Vendor</Th>
                <Th>Quotation Number</Th>
                <Th isNumeric>Quoted Amount</Th>
                <Th>Status</Th>
                <Th>Availability</Th>
                <Th>Document</Th>
                <Th>Notes</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {visibleProposals.map((proposal) => {
                const isExpanded = expandedProposalIds.includes(proposal._id);
                const proposalLineItems = proposal.lineItems || [];

                return (
                  <React.Fragment key={proposal._id}>
                    <Tr
                      bg={
                        proposal.status === "APPROVED" ? "green.50" : undefined
                      }
                      opacity={proposal.isActive === false ? 0.7 : 1}
                    >
                      <Td>
                        <IconButton
                          icon={
                            isExpanded ? <BsChevronDown /> : <BsChevronRight />
                          }
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleProposalExpanded(proposal._id)}
                          aria-label={
                            isExpanded ? "Collapse details" : "Expand details"
                          }
                          isDisabled={proposalLineItems.length === 0}
                        />
                      </Td>
                      <Td>
                        <HStack>
                          {proposal.status === "APPROVED" && (
                            <BsTrophy color="gold" />
                          )}
                          <Text
                            fontWeight={
                              proposal.status === "APPROVED" ? "bold" : "normal"
                            }
                          >
                            {proposal.vendorId?.name || "Unknown Vendor"}
                          </Text>
                        </HStack>
                      </Td>
                      <Td>{proposal.poNumber}</Td>
                      <Td isNumeric fontWeight="semibold">
                        {formatCurrency(
                          proposal.quotedAmount,
                          proposal.currency,
                        )}
                      </Td>
                      <Td>{getStatusBadge(proposal.status)}</Td>
                      <Td>{getActiveBadge(proposal.isActive !== false)}</Td>
                      <Td>
                        {proposal.poDocumentUrl ||
                        proposal.poDocumentAttachmentUrl ? (
                          <VStack align="start" spacing={1}>
                            {proposal.poDocumentUrl ? (
                              <Link
                                href={proposal.poDocumentUrl}
                                isExternal
                                color="blue.500"
                              >
                                <HStack>
                                  <BsFileEarmarkText />
                                  <Text fontSize="sm">Open URL</Text>
                                </HStack>
                              </Link>
                            ) : null}
                            {proposal.poDocumentAttachmentUrl ? (
                              <Link
                                href={proposal.poDocumentAttachmentUrl}
                                isExternal
                                color="teal.500"
                              >
                                <HStack>
                                  <BsFileEarmarkText />
                                  <Text fontSize="sm">Open File</Text>
                                </HStack>
                              </Link>
                            ) : null}
                          </VStack>
                        ) : (
                          <Text color="gray.400" fontSize="sm">
                            N/A
                          </Text>
                        )}
                      </Td>
                      <Td maxW="200px" isTruncated>
                        <Tooltip label={proposal.notes || "No notes"}>
                          <Text fontSize="sm">{proposal.notes || "-"}</Text>
                        </Tooltip>
                      </Td>
                      <Td>
                        <VStack align="stretch" spacing={2}>
                          <Button
                            size="xs"
                            leftIcon={<BsEye />}
                            variant="outline"
                            onClick={() => openDetailModal(proposal)}
                          >
                            Details
                          </Button>
                          <Select
                            size="sm"
                            value={
                              proposal.isActive === false
                                ? "inactive"
                                : "active"
                            }
                            onChange={(e) =>
                              handleActiveStatusChange(proposal, e.target.value)
                            }
                            isDisabled={
                              actionLoading === `toggle-${proposal._id}`
                            }
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </Select>
                          {canManageProposals &&
                            proposal.status === "SUBMITTED" &&
                            proposal.isActive !== false && (
                              <VStack spacing={2} width="full">
                                <Button
                                  leftIcon={<BsCheckCircle />}
                                  size="sm"
                                  colorScheme="green"
                                  width="full"
                                  onClick={() => handleApproveClick(proposal)}
                                  isLoading={actionLoading === proposal._id}
                                >
                                  Approve
                                </Button>
                                <Button
                                  leftIcon={<BsXCircle />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="outline"
                                  width="full"
                                  onClick={() => handleRejectClick(proposal)}
                                  isLoading={actionLoading === proposal._id}
                                >
                                  Reject
                                </Button>
                              </VStack>
                            )}
                        </VStack>
                      </Td>
                    </Tr>
                    {isExpanded && proposalLineItems.length > 0 && (
                      <Tr
                        bg={
                          proposal.status === "APPROVED"
                            ? "green.50"
                            : "gray.50"
                        }
                      >
                        <Td colSpan={9} pl={12}>
                          <Box py={3}>
                            <Text fontWeight="semibold" mb={3}>
                              Submitted Line Item Breakdown
                            </Text>
                            <Box
                              borderWidth="1px"
                              borderRadius="md"
                              overflowX="auto"
                              bg="white"
                            >
                              <Table size="sm" variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th>Item</Th>
                                    <Th>Description</Th>
                                    <Th isNumeric>Qty</Th>
                                    <Th>Unit</Th>
                                    <Th isNumeric>Unit Price</Th>
                                    <Th isNumeric>Total</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {proposalLineItems.map((item, index) => (
                                    <Tr
                                      key={
                                        item._id ||
                                        item.requestLineItemId ||
                                        `${proposal._id}-${index}`
                                      }
                                    >
                                      <Td>{item.itemName || "-"}</Td>
                                      <Td>{item.description || "-"}</Td>
                                      <Td isNumeric>{item.quantity || 0}</Td>
                                      <Td>{item.unit || "-"}</Td>
                                      <Td isNumeric>
                                        {formatCurrency(
                                          item.unitPrice || 0,
                                          proposal.currency,
                                        )}
                                      </Td>
                                      <Td isNumeric fontWeight="semibold">
                                        {formatCurrency(
                                          item.totalPrice || 0,
                                          proposal.currency,
                                        )}
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </Box>
                            <HStack justify="flex-end" mt={2}>
                              <Text fontWeight="semibold">
                                Total:{" "}
                                {formatCurrency(
                                  getLineItemsTotal(proposalLineItems),
                                  proposal.currency,
                                )}
                              </Text>
                            </HStack>
                          </Box>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Request Status Info */}
      {requestStatus === "PO_SELECTED" && (
        <Box
          mt={4}
          p={4}
          bg="green.50"
          borderRadius="md"
          borderWidth="1px"
          borderColor="green.200"
        >
          <HStack>
            <BsCheckCircle color="green" />
            <Text color="green.700" fontWeight="medium">
              Vendor has been selected. This procurement request is complete.
            </Text>
          </HStack>
        </Box>
      )}

      {/* Add Vendor PO Modal */}
      <AddVendorPOModal
        isOpen={isOpen}
        onClose={onClose}
        requestId={requestId}
        onSuccess={handleAddSuccess}
        requestLineItems={requestLineItems}
      />

      {/* Proposal Detail Modal */}
      <Modal
        isOpen={detailModalOpen}
        onClose={closeDetailModal}
        isCentered
        size="full"
        aria-labelledby="quotation-title"
      >
        <ModalOverlay />
        <ModalContent
          role="dialog"
          aria-modal="true"
          maxW={{ base: "full", md: "95%", lg: "900px" }}
          mx={{ base: 0, md: 4 }}
          borderRadius={{ base: 0, md: "lg" }}
        >
          <ModalHeader id="quotation-title" px={6} pt={6} pb={4}>
            Quotation Details
          </ModalHeader>
          <ModalCloseButton
            boxSize={8}
            fontSize="16px"
            borderRadius="full"
            _hover={{ bg: "gray.100" }}
          />
          <ModalBody px={6} pb={6}>
            {detailProposal ? (
              <VStack align="stretch" spacing={4}>
                <Flex
                  justify="space-between"
                  align={{ base: "start", md: "center" }}
                  wrap="wrap"
                  gap={3}
                >
                  <VStack align="start" spacing={1}>
                    <Text fontSize="16px" fontWeight="semibold">
                      {detailProposal.vendorId?.name || "Unknown Vendor"}
                    </Text>
                    <Text fontSize="14px" color="gray.600">
                      Quotation #: {detailProposal.poNumber || "-"}
                    </Text>
                    <Text fontSize="12px" color="gray.500">
                      Submitted: {formatDateTime(detailProposal.createdAt)}
                    </Text>
                  </VStack>
                  <Text fontSize="20px" fontWeight="bold" color="gray.800">
                    {formatCurrency(
                      detailProposal.quotedAmount,
                      detailProposal.currency,
                    )}
                  </Text>
                </Flex>

                <HStack spacing={2}>
                  {getStatusBadge(detailProposal.status)}
                  {getActiveBadge(detailProposal.isActive !== false)}
                </HStack>

                <HStack spacing={2} align="center">
                  <BsFileEarmarkText />
                  {detailProposal.poDocumentUrl ||
                  detailProposal.poDocumentAttachmentUrl ? (
                    <HStack spacing={3} flexWrap="wrap">
                      {detailProposal.poDocumentUrl ? (
                        <Link
                          href={detailProposal.poDocumentUrl}
                          isExternal
                          color="blue.600"
                          fontWeight="500"
                          _hover={{ textDecoration: "underline" }}
                        >
                          <HStack spacing={1}>
                            <Text>View URL</Text>
                            <BsBoxArrowUpRight size={12} />
                          </HStack>
                        </Link>
                      ) : null}
                      {detailProposal.poDocumentAttachmentUrl ? (
                        <Link
                          href={detailProposal.poDocumentAttachmentUrl}
                          isExternal
                          color="teal.600"
                          fontWeight="500"
                          _hover={{ textDecoration: "underline" }}
                        >
                          <HStack spacing={1}>
                            <Text>
                              {detailProposal.poDocumentAttachmentName ||
                                "View Uploaded File"}
                            </Text>
                            <BsBoxArrowUpRight size={12} />
                          </HStack>
                        </Link>
                      ) : null}
                    </HStack>
                  ) : (
                    <Text color="gray.500">Document not available</Text>
                  )}
                </HStack>

                <Box bg="gray.50" borderRadius="6px" p={3}>
                  <Text fontSize="13px" fontWeight="600" mb={1}>
                    Notes
                  </Text>
                  <Text fontSize="13px" color="gray.700">
                    {detailProposal.notes || "No notes provided."}
                  </Text>
                </Box>

                <Divider />

                <Text fontWeight="semibold">Submitted Line Item Breakdown</Text>
                {detailProposal.lineItems?.length > 0 ? (
                  <Box borderWidth="1px" borderRadius="md" overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead bg="gray.100">
                        <Tr>
                          <Th
                            fontSize="12px"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                            fontWeight="600"
                          >
                            Item
                          </Th>
                          <Th
                            fontSize="12px"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                            fontWeight="600"
                          >
                            Description
                          </Th>
                          <Th
                            textAlign="center"
                            fontSize="12px"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                            fontWeight="600"
                          >
                            Qty
                          </Th>
                          <Th
                            textAlign="center"
                            fontSize="12px"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                            fontWeight="600"
                          >
                            Unit
                          </Th>
                          <Th
                            isNumeric
                            fontSize="12px"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                            fontWeight="600"
                          >
                            Unit Price
                          </Th>
                          <Th
                            isNumeric
                            fontSize="12px"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                            fontWeight="600"
                          >
                            Total
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {detailProposal.lineItems.map((item, index) => (
                          <Tr
                            _hover={{ bg: "gray.50" }}
                            key={
                              item._id ||
                              item.requestLineItemId ||
                              `${detailProposal._id}-${index}`
                            }
                          >
                            <Td py={2.5}>{item.itemName || "-"}</Td>
                            <Td py={2.5}>{item.description || "-"}</Td>
                            <Td py={2.5} textAlign="center">
                              {item.quantity || 0}
                            </Td>
                            <Td py={2.5} textAlign="center">
                              {item.unit || "-"}
                            </Td>
                            <Td
                              isNumeric
                              py={2.5}
                              sx={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {formatCurrency(
                                item.unitPrice || 0,
                                detailProposal.currency,
                              )}
                            </Td>
                            <Td
                              isNumeric
                              py={2.5}
                              fontWeight="semibold"
                              sx={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {formatCurrency(
                                item.totalPrice || 0,
                                detailProposal.currency,
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                      <Tfoot>
                        <Tr borderTop="2px solid" borderColor="gray.200">
                          <Td
                            colSpan={5}
                            textAlign="right"
                            fontSize="16px"
                            fontWeight="600"
                          >
                            Total
                          </Td>
                          <Td
                            isNumeric
                            fontSize="16px"
                            fontWeight="600"
                            sx={{ fontVariantNumeric: "tabular-nums" }}
                          >
                            {formatCurrency(
                              getLineItemsTotal(detailProposal.lineItems),
                              detailProposal.currency,
                            )}
                          </Td>
                        </Tr>
                      </Tfoot>
                    </Table>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    No line item breakdown submitted for this quotation.
                  </Text>
                )}
              </VStack>
            ) : null}
          </ModalBody>
          <ModalFooter px={6} pb={6}>
            <Button
              h="40px"
              px={4.5}
              borderRadius="6px"
              onClick={closeDetailModal}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Approve Confirmation Dialog */}
      <AlertDialog
        isOpen={approveConfirmOpen}
        leastDestructiveRef={approveCancelRef}
        onClose={() => setApproveConfirmOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Vendor Selection
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="stretch" spacing={3}>
                <Text>
                  You are about to select{" "}
                  <strong>{selectedProposal?.vendorId?.name}</strong> for this
                  procurement request.
                </Text>
                <Text>
                  Quoted Amount:{" "}
                  <strong>
                    {formatCurrency(
                      selectedProposal?.quotedAmount,
                      selectedProposal?.currency,
                    )}
                  </strong>
                </Text>
                <Text color="orange.600" fontWeight="medium">
                  ⚠️ This action will:
                </Text>
                <Box pl={4}>
                  <Text fontSize="sm">• Approve this vendor's proposal</Text>
                  <Text fontSize="sm">
                    • Auto-reject all other pending proposals
                  </Text>
                  <Text fontSize="sm">
                    • Mark the request as PO_SELECTED (final)
                  </Text>
                </Box>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={approveCancelRef}
                onClick={() => setApproveConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={handleApproveConfirm}
                ml={3}
                isLoading={actionLoading}
              >
                Confirm & Select Vendor
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Reject Dialog */}
      <AlertDialog
        isOpen={rejectDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setRejectDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reject Proposal
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="stretch" spacing={4}>
                <Text>
                  You are rejecting the proposal from{" "}
                  <strong>{selectedProposal?.vendorId?.name}</strong>.
                </Text>
                <FormControl isRequired>
                  <FormLabel>Rejection Reason</FormLabel>
                  <Input
                    placeholder="e.g., Quote is too high"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  />
                </FormControl>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setRejectDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleRejectConfirm}
                ml={3}
                isLoading={actionLoading}
                isDisabled={!rejectionReason.trim()}
              >
                Reject Proposal
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default VendorPOProposalList;
